import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  Grid,
  CircularProgress,
  alpha,
  useTheme,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { QK } from "../../../../../../base/qk";
import { getSecAssessmentByID, updateSecAssessment } from "../../../../../../api/services/observations";
import { enqueueSnackbar } from "notistack";
import CustomTextArea from "../../../../../CustomForm/CustomTextArea";
import CustomDateField from "../../../../../FormFields/CustomDateField";
import CustomCheckbox from "../../../../../CustomForm/CustomCheckbox";
import { bowelMovement } from "./bowelMovement.config";

const EditBowelMovementModal = ({ show, handleClose, selectedID }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    type: "",
    notes: "",
    date_time: "",
    handover: false,
    manager: false,
  });

  const [errors, setErrors] = useState({});

  // Fetching bowel movement entry based on the selected ID
  const { data: assessment, isLoading } = useQuery(
    [QK.bowelMovement, selectedID],
    () => getSecAssessmentByID(selectedID),
    {
      enabled: !!selectedID && show,
      onSuccess: (data) => {
        if (data) {
          setFormData({
            type: data.type || "",
            notes: data.notes || "",
            date_time: data.date_time || "",
            handover: data.handover || false,
            manager: data.manager || false,
          });
          setErrors({});
        }
      },
    }
  );

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setFormData({
        type: "",
        notes: "",
        date_time: "",
        handover: false,
        manager: false,
      });
      setErrors({});
    }
  }, [show]);

  // Mutation for updating the bowel movement entry
  const updateMutation = useMutation(
    (newData) => updateSecAssessment(selectedID, newData),
    {
      onSuccess: () => {
        enqueueSnackbar("Bowel movement record updated successfully", { 
          variant: "success" 
        });
        queryClient.invalidateQueries([QK.bowelMovements]);
        queryClient.invalidateQueries([QK.bowelMovement, selectedID]);
        handleClose();
      },
      onError: (error) => {
        enqueueSnackbar(
          error?.response?.data?.message || "Failed to update bowel movement record",
          { variant: "error" }
        );
      },
    }
  );

  const validateForm = useCallback(() => {
    const newErrors = {};
    
    if (!formData.type) {
      newErrors.type = "Stool type is required";
    }
    if (!formData.date_time) {
      newErrors.date_time = "Date and time is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user interacts
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  }, [errors]);

  const handleCheckBox = useCallback((name, checked) => {
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (validateForm()) {
      updateMutation.mutate(formData);
    }
  }, [formData, updateMutation, validateForm]);

  const handleDialogClose = useCallback(() => {
    setFormData({
      type: "",
      notes: "",
      date_time: "",
      handover: false,
      manager: false,
    });
    setErrors({});
    handleClose();
  }, [handleClose]);

  const isSubmitting = updateMutation.isLoading;

  if (isLoading) {
    return (
      <Dialog open={show} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={show}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="edit-bowel-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
        },
      }}
    >
      <DialogTitle id="edit-bowel-dialog-title" sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: alpha(theme.palette.success.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EditIcon sx={{ color: theme.palette.success.main, fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                Edit Bowel Movement
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                Update bowel movement record details
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleDialogClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
            disabled={isSubmitting}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.4 }} />
      </DialogTitle>

      <form onSubmit={handleSubmit} noValidate>
        <DialogContent sx={{ p: 2.5, pt: 2 }}>
          <Grid container spacing={3}>
            {/* Stool Type Selection with larger images */}
            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 3,
                  bgcolor: "#F8FAFC",
                  borderRadius: "12px",
                  border: errors.type ? "2px solid #DC2626" : "1px solid #E2E8F0",
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 3, color: "#1A1A2E" }}>
                  Stool Type *
                </Typography>
                {errors.type && (
                  <Typography variant="caption" color="error" sx={{ display: "block", mb: 2 }}>
                    {errors.type}
                  </Typography>
                )}
                <RadioGroup
                  name="type"
                  value={formData.type || ""}
                  onChange={handleChange}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  {bowelMovement.map((item) => (
                    <FormControlLabel
                      key={item.type}
                      value={item.type}
                      control={<Radio />}
                      label={
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                          <img 
                            src={item.image} 
                            alt={item.type} 
                            style={{ 
                              width: 80, 
                              height: 80, 
                              objectFit: "contain",
                              borderRadius: "12px",
                              backgroundColor: "white",
                              padding: "8px",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
                            }}
                          />
                          <Box sx={{ textAlign: "center" }}>
                            <Typography variant="body1" fontWeight={600} color="#1A1A2E">
                              {item.type}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ display: "block" }}>
                              {item.title}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      sx={{
                        flex: "1 0 auto",
                        minWidth: "140px",
                        maxWidth: "180px",
                        p: 2,
                        borderRadius: "12px",
                        border: formData.type === item.type 
                          ? `2px solid ${theme.palette.success.main}` 
                          : "2px solid #E2E8F0",
                        bgcolor: formData.type === item.type 
                          ? alpha(theme.palette.success.main, 0.04) 
                          : "white",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: "#F1F5F9",
                          borderColor: "#94A3B8",
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        },
                        "&.Mui-checked": {
                          borderColor: theme.palette.success.main,
                          bgcolor: alpha(theme.palette.success.main, 0.04),
                          boxShadow: `0 0 0 4px ${alpha(theme.palette.success.main, 0.1)}`,
                        },
                      }}
                    />
                  ))}
                </RadioGroup>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <CustomDateField
                name="date_time"
                label="Date & Time *"
                value={formData.date_time || ""}
                onChange={handleChange}
                required
                fullWidth
                error={!!errors.date_time}
                helperText={errors.date_time}
              />
            </Grid>

            <Grid item xs={12}>
              <CustomTextArea
                label="Notes"
                name="notes"
                value={formData.notes || ""}
                onChange={handleChange}
                rows={3}
                fullWidth
                placeholder="Add any additional notes..."
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                <CustomCheckbox
                  label="Share with Manager"
                  name="manager"
                  checked={formData.manager}
                  onClick={(checked) => handleCheckBox("manager", checked)}
                />
                <CustomCheckbox
                  label="Handover"
                  name="handover"
                  checked={formData.handover}
                  onClick={(checked) => handleCheckBox("handover", checked)}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5 }}>
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            disabled={isSubmitting}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "10px",
              borderColor: "#D0D5DD",
              color: "#344054",
              "&:hover": {
                bgcolor: "#F9FAFB",
                borderColor: "#98A2B3",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              bgcolor: "#059669",
              px: 3,
              "&:hover": {
                bgcolor: "#047857",
                boxShadow: "0px 4px 12px rgba(5, 150, 105, 0.3)",
              },
            }}
          >
            {isSubmitting ? "Saving..." : "Update Record"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default React.memo(EditBowelMovementModal);